import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    TextField,
} from "@mui/material";
import { toast } from "react-toastify";
import { getAssessmentsByBatch, updateAssessment } from "../../API/student-mgmt";
import { IKContext, IKUpload } from "imagekitio-react";
import { imageKitPublicKey, imageKitUrlEndpoint } from "../../helper";

type Assessment = {
    _id: string;
    student?: { name: string; _id: string };
    category: string;
    assessmentLevel: string;
    certificationLevel: string;
    certificateUrl?: string;
    reportCardUrl?: string;
};

const uri =
    process.env.NODE_ENV === "development"
        ? process.env.REACT_APP_DEV_URL
        : process.env.REACT_APP_URL;

const ViewAssessmentDetails = ({ open, onClose, batchId }) => {
    const [loading, setLoading] = useState(true);
    const [assessments, setAssessments] = useState<Assessment[]>([]);
    const [searchQuery, setSearchQuery] = useState("");

    useEffect(() => {
        if (open && batchId) {
            fetchAssessmentDetails();
        }
    }, [open, batchId]);

    const fetchAssessmentDetails = async () => {
        setLoading(true);
        try {
            const response = await getAssessmentsByBatch(batchId);
            if (response && Array.isArray(response.data)) {
                setAssessments(response.data);
            } else {
                toast.error("Invalid data structure received from server");
                setAssessments([]);
            }
        } catch (error) {
            toast.error("Error fetching assessment details");
            setAssessments([]);
        } finally {
            setLoading(false);
        }
    };

    const onUploadSuccess = async (res: any, assessmentId: string, type: "certificate" | "reportCard") => {
        const updateKey = type === "certificate" ? "certificateUrl" : "reportCardUrl";

        const updatedAssessments = assessments.map((assessment) => {
            if (assessment._id === assessmentId) {
                return {
                    ...assessment,
                    [updateKey]: res.url,
                };
            }
            return assessment;
        });
        setAssessments(updatedAssessments);

        try {
            await updateAssessment(assessmentId, { [updateKey]: res.url });
            toast.success(`${type === "certificate" ? "Certificate" : "Report Card"} uploaded successfully!`);
        } catch (error) {
            toast.error("Error updating assessment record");
        }
    };

    const onUploadError = () => {
        toast.error("Error uploading document");
    };

    const filteredAssessments = assessments.filter(
        (assessment) =>
            assessment.student?.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            assessment.category.toLowerCase().includes(searchQuery.toLowerCase())
    );


    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>Assessment Details</DialogTitle>
            <DialogContent>
                <TextField
                    fullWidth
                    label="Search by Student or Category"
                    variant="outlined"
                    margin="dense"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />

                {loading ? (
                    <div style={{ display: "flex", justifyContent: "center", padding: "20px" }}>
                        <CircularProgress />
                    </div>
                ) : assessments.length > 0 ? (
                    <TableContainer component={Paper} style={{ marginTop: "15px" }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Student Name</TableCell>
                                    <TableCell>Category</TableCell>
                                    <TableCell>Assessment Level</TableCell>
                                    <TableCell>Certification Level</TableCell>
                                    <TableCell>Certificate</TableCell>
                                    <TableCell>Report Card</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredAssessments.length > 0 ? (
                                    filteredAssessments.map((assessment) => (
                                        <TableRow key={assessment._id}>
                                            <TableCell>{assessment.student?.name || "N/A"}</TableCell>
                                            <TableCell>{assessment.category || "N/A"}</TableCell>
                                            <TableCell>{assessment.assessmentLevel || "N/A"}</TableCell>
                                            <TableCell>{assessment.certificationLevel || "N/A"}</TableCell>
                                            <TableCell>
                                                {assessment.certificateUrl ? (
                                                    <Button
                                                        href={assessment.certificateUrl}
                                                        target="_blank"
                                                        variant="contained"
                                                        color="primary"
                                                    >
                                                        View
                                                    </Button>
                                                ) : (
                                                    <IKContext
                                                        publicKey={imageKitPublicKey}
                                                        urlEndpoint={imageKitUrlEndpoint}
                                                        authenticationEndpoint={uri + `card/auth`}
                                                    >
                                                        <IKUpload
                                                            fileName="certificate-upload"
                                                            onError={onUploadError}
                                                            onSuccess={(res) => onUploadSuccess(res, assessment._id, "certificate")}
                                                        />
                                                    </IKContext>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {assessment.reportCardUrl ? (
                                                    <Button
                                                        href={assessment.reportCardUrl}
                                                        target="_blank"
                                                        variant="contained"
                                                        color="secondary"
                                                    >
                                                        View
                                                    </Button>
                                                ) : (
                                                    <IKContext
                                                        publicKey={imageKitPublicKey}
                                                        urlEndpoint={imageKitUrlEndpoint}
                                                        authenticationEndpoint={uri + `card/auth`}
                                                    >
                                                        <IKUpload
                                                            fileName="report-card-upload"
                                                            onError={onUploadError}
                                                            onSuccess={(res) => onUploadSuccess(res, assessment._id, "reportCard")}
                                                        />
                                                    </IKContext>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={6} style={{ textAlign: "center" }}>
                                            No matching records found.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>

                        </Table>
                    </TableContainer>
                ) : (
                    <p>No assessment details available.</p>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ViewAssessmentDetails;
